/**
 * Entry point: tiering, then renderer, then loop.
 *
 * Startup:
 * 1. detectDevice(). With no WebGPU and no WebGL2, show the poster and stop.
 * 2. Build GPU resources at 'medium' and start the loop (first paint).
 * 3. Benchmark ~2 s at medium, then pick the real tier with chooseInitialTier.
 * 4. From then on the governor adjusts the tier unless the user overrides it.
 */
import {
  DEFAULT_AIR_DENSITY_KG_M3,
  DEFAULT_CLOUDS,
  DEFAULT_GUST,
  DEFAULT_SECONDARY,
  DEFAULT_STEERING,
  DEFAULT_STORM,
  DEFAULT_TURBULENCE,
  DEFAULT_VERTICAL_STRETCH,
  DEFAULT_TIME_SCALE,
  DOMAIN_RADIUS_IN_RMAX,
  REDUCED_MOTION_TIME_SCALE,
  RENDER_UNITS_PER_M,
} from './config/constants';
import { MAX_DEBRIS_ANY_TIER, TIERS, type TierId } from './config/tiers';
import { BenchRun } from './core/benchRun';
import { BenchSweep } from './core/benchSweep';
import { DEFAULT_GOVERNOR_OPTIONS, Governor } from './core/governor';
import { createGpuTimer, governorSignalMs, type GpuTimer } from './core/gpuTiming';
import { createFluidClient } from './core/fluidClient';
import type { CloudLayerParams } from './physics/cloudFluid';
import { createClouds, type CloudsHandle } from './render/clouds';
import { createCowLod, type CowLodHandle } from './render/cowLod';
import { COW_VISUAL_RADIUS_KM, makeCowTexture } from './render/scene';
import {
  createEllipsoidCowState,
  currentSupportHeight,
  defaultEllipsoidCow,
  resetEllipsoidCow,
  stepEllipsoidCow,
} from './physics/realisticCow';
import { installLifecycle, prefersReducedMotion } from './core/lifecycle';
import { createLoop, DEFAULT_FIXED_DT_S, DEFAULT_MAX_SUBSTEPS } from './core/loop';
import {
  BENCHMARK_DURATION_MS,
  chooseInitialTier,
  detectDevice,
  FrameSampler,
} from './core/tiering';
import { Rng } from './core/rng';
import type { HollandParams } from './physics/hollandProfile';
import { createOuProcess, sigmaForStationaryStd } from './physics/ouProcess';
import { createTranslationParams, setSteering } from './physics/stormTranslation';
import {
  COW_BODY_DENSITY_KG_M3,
  COW_MASS_KG,
  createRigidBodyState,
  equivalentSphereRadius,
  resetRigidBody,
  stepRigidBody,
  type CowBodyParams,
} from './physics/rigidBodyDrag';
import { createTracers, type Tracers } from './physics/tracers';
import { createWindField, type WindSample } from './physics/windField';
import { createGpuTracers, type GpuTracers } from './physics/gpu/gpuTracers';
import { runGpuValidation } from './physics/gpu/validateGpu';
import { createDebris, type DebrisHandle } from './render/debrisInstanced';
import { createRenderer, type RendererHandle } from './render/renderer';
import { createScene, type SceneHandle } from './render/scene';
import { createVortexDebug, type VortexDebugHandle } from './render/vortexDebug';
import { createControls, type ControlKey, type ControlState } from './ui/controls';
import { createBenchPanel } from './ui/benchPanel';
import { createFinancePanel } from './ui/financePanel';
import { createPerfHud } from './ui/perfHud';

const SEED = 20261019;

/** The cow is released at rest from this height, due east of the eye at Rmax. */
const COW_DROP_HEIGHT_M = 8_000;
/** Respawn the cow after this much storm time, e.g. if it settles in the calm eye. */
const COW_MAX_AGE_S = 3 * 3600;
/**
 * When the storm center drifts this far from the origin, shift every
 * position back. Keeps Float32 tracer coordinates precise on long runs.
 */
const REBASE_DISTANCE_M = 1_000_000;

/** `?backend=webgl2` forces the WebGL2 backend, for checking parity across backends. */
const URL_PARAMS = new URLSearchParams(location.search);
const FORCE_WEBGL = URL_PARAMS.get('backend') === 'webgl2';
/** `?physics=cpu` keeps debris on the CPU reference path. Default is GPU compute. */
const GPU_PHYSICS = URL_PARAMS.get('physics') !== 'cpu';
/** `?validate=gpu` runs the phase 4 CPU-vs-GPU comparison at startup. */
const VALIDATE_GPU = URL_PARAMS.get('validate') === 'gpu';
/** `?bench=sweep` runs the phase 5 calibration sweep across all tiers. */
const BENCH_SWEEP = URL_PARAMS.get('bench') === 'sweep';
/** `?bench=run` runs the 5-minute steady-state benchmark (bench/README.md). */
const BENCH_RUN = URL_PARAMS.get('bench') === 'run';
/** Seconds; overridable for quick checks, which the output then labels as short runs. */
const BENCH_WARMUP_S = Number(URL_PARAMS.get('warm') ?? 60);
const BENCH_MEASURE_S = Number(URL_PARAMS.get('measure') ?? 300);

function byId(id: string): HTMLElement {
  const e = document.getElementById(id);
  if (!e) throw new Error(`#${id} missing from index.html`);
  return e;
}

function showPoster(message: string): void {
  const poster = byId('poster');
  byId('poster-msg').textContent = message;
  poster.hidden = false;
}

function tracerGeometry(rMaxM: number): {
  domainRadiusM: number;
  spawnInnerM: number;
  spawnOuterM: number;
} {
  return {
    domainRadiusM: DOMAIN_RADIUS_IN_RMAX * rMaxM,
    spawnInnerM: 0.3 * rMaxM,
    spawnOuterM: (DOMAIN_RADIUS_IN_RMAX - 1) * rMaxM,
  };
}

async function main(): Promise<void> {
  const profile = await detectDevice();
  if (profile.backend === 'none') {
    showPoster(
      'This browser exposes neither WebGPU nor WebGL2, so the simulation cannot run here. ' +
        'Try a current Chrome, Edge, Firefox, or Safari.',
    );
    return;
  }

  const reducedMotion = prefersReducedMotion();
  const state: ControlState = {
    cowMode: 'spherical',
    deltaPPa: DEFAULT_STORM.pAmbientPa - DEFAULT_STORM.pCentralPa,
    rMaxM: DEFAULT_STORM.rMaxM,
    B: DEFAULT_STORM.B,
    latitudeDeg: DEFAULT_STORM.latitudeDeg,
    timeScale: reducedMotion ? REDUCED_MOTION_TIME_SCALE : DEFAULT_TIME_SCALE,
    verticalStretch: DEFAULT_VERTICAL_STRETCH,
    steerMps: DEFAULT_STEERING.speedMps,
    steerHeadingDeg: DEFAULT_STEERING.headingDeg,
    inflowPeakMps: DEFAULT_SECONDARY.inflowPeakMps,
    turbulenceMps: DEFAULT_TURBULENCE.amplitudeMps,
    debugOverlay: true,
    clouds: true,
    hudVisible: true,
    paused: reducedMotion,
    tierOverride: 'auto',
  };

  const holland: HollandParams = {
    pAmbientPa: DEFAULT_STORM.pAmbientPa,
    pCentralPa: DEFAULT_STORM.pAmbientPa - state.deltaPPa,
    rMaxM: state.rMaxM,
    B: state.B,
    latitudeDeg: state.latitudeDeg,
    airDensity: DEFAULT_AIR_DENSITY_KG_M3,
  };
  const wind = createWindField(holland, {
    translation: setSteering(createTranslationParams(), state.steerMps, state.steerHeadingDeg),
    inflow: {
      rMaxM: holland.rMaxM,
      inflowDepthM: DEFAULT_SECONDARY.inflowDepthM,
      topM: DEFAULT_SECONDARY.topM,
      inflowPeakMps: state.inflowPeakMps,
    },
    noise: {
      seed: SEED + 2,
      ...DEFAULT_TURBULENCE,
      amplitudeMps: state.turbulenceMps,
      rPeakM: holland.rMaxM,
    },
    tangentialFullHeightM: DEFAULT_SECONDARY.tangentialFullHeightM,
  });
  const gustRng = new Rng(SEED + 3);
  const gust = createOuProcess(
    {
      mu: DEFAULT_GUST.mu,
      theta: DEFAULT_GUST.theta,
      sigma: sigmaForStationaryStd(DEFAULT_GUST.stationaryStd, DEFAULT_GUST.theta),
    },
    DEFAULT_GUST.mu,
  );
  const center: WindSample = { x: 0, y: 0, z: 0 };

  // CPU buffers sized for the largest tier once; tiers only change `count`.
  const debrisTracers: Tracers = createTracers({
    capacity: MAX_DEBRIS_ANY_TIER,
    seed: SEED,
    ...tracerGeometry(state.rMaxM),
    spawnMinHeightM: 50,
    spawnMaxHeightM: 3000,
    maxAgeS: 4 * 3600,
  });
  // The cow: a rigid sphere under gravity and drag. Respawns when it leaves the domain.
  const cowParams: CowBodyParams = {
    mode: state.cowMode,
    massKg: COW_MASS_KG,
    radiusM: equivalentSphereRadius(COW_MASS_KG, COW_BODY_DENSITY_KG_M3),
    airDensity: holland.airDensity,
  };
  const cow = createRigidBodyState();
  // Realistic mode: a tumbling ellipsoid. Its position is mirrored into
  // `cow` after each step, so respawn, rebase, and rendering share one path.
  const eParams = defaultEllipsoidCow(COW_MASS_KG, COW_BODY_DENSITY_KG_M3, holland.airDensity);
  const eCow = createEllipsoidCowState();
  const cowRng = new Rng(SEED + 4);
  /** Display km per physical metre: same exaggeration as the sphere. */
  const cowKmPerM = COW_VISUAL_RADIUS_KM / cowParams.radiusM;
  let cowRespawned = true;
  let cowAgeS = 0;
  let simTimeS = 0;
  function respawnCow(): void {
    wind.stormCenter(simTimeS, center);
    resetRigidBody(cow, center.x + state.rMaxM, COW_DROP_HEIGHT_M, center.z);
    // Uniform random orientation (Shoemake 1992 subgroup method) and a small spin.
    const u1 = cowRng.next();
    const u2 = cowRng.range(0, 2 * Math.PI);
    const u3 = cowRng.range(0, 2 * Math.PI);
    const a = Math.sqrt(1 - u1);
    const c = Math.sqrt(u1);
    resetEllipsoidCow(
      eCow,
      cow.posX,
      cow.posY,
      cow.posZ,
      [a * Math.sin(u2), a * Math.cos(u2), c * Math.sin(u3), c * Math.cos(u3)],
      [cowRng.range(-0.3, 0.3), cowRng.range(-0.3, 0.3), cowRng.range(-0.3, 0.3)],
    );
    cowRespawned = true;
    cowAgeS = 0;
  }
  respawnCow();

  /** Shift the world so the storm center is back at the origin. Rare; not hot path. */
  function rebase(): void {
    wind.stormCenter(simTimeS, center);
    const sx = center.x;
    const sz = center.z;
    const tr = wind.translation;
    tr.originXM -= sx;
    tr.originZM -= sz;
    const { posX, posZ, prevX, prevZ } = debrisTracers;
    for (let i = 0; i < debrisTracers.count; i++) {
      posX[i] = (posX[i] as number) - sx;
      posZ[i] = (posZ[i] as number) - sz;
      prevX[i] = (prevX[i] as number) - sx;
      prevZ[i] = (prevZ[i] as number) - sz;
    }
    gpu?.tracers?.shift(sx, sz);
    cow.posX -= sx;
    cow.posZ -= sz;
    cow.prevX -= sx;
    cow.prevZ -= sz;
    eCow.posX -= sx;
    eCow.posZ -= sz;
    eCow.prevX -= sx;
    eCow.prevZ -= sz;
    cowRespawned = true;
  }

  const container = byId('app');
  const overlay = byId('overlay');
  const inset = byId('profile-inset') as HTMLCanvasElement;
  const hud = createPerfHud(byId('hud'));
  const playBtn = byId('play') as HTMLButtonElement;

  let activeTier: TierId = 'medium';
  const governor = new Governor(activeTier);
  let gpu: {
    r: RendererHandle;
    s: SceneHandle;
    debris: DebrisHandle;
    vortex: VortexDebugHandle;
    /** GPU debris; null on the CPU path. */
    tracers: GpuTracers | null;
    timer: GpuTimer;
    clouds: CloudsHandle;
    cowLod: CowLodHandle;
  } | null = null;

  const fluid = createFluidClient();
  function cloudParams(n: number): CloudLayerParams {
    return {
      holland: { ...holland },
      inflow: wind.inflow
        ? { ...wind.inflow.params }
        : { rMaxM: holland.rMaxM, inflowDepthM: 1000, topM: 15_000, inflowPeakMps: 0 },
      tangentialFullHeightM: DEFAULT_SECONDARY.tangentialFullHeightM,
      cloudHeightM: DEFAULT_CLOUDS.layerHeightM,
      n,
      sizeM: 2 * DOMAIN_RADIUS_IN_RMAX * state.rMaxM,
      sourcePeakPerS: DEFAULT_CLOUDS.sourcePeakPerS,
      densityDecayS: DEFAULT_CLOUDS.densityDecayS,
      viscosity: DEFAULT_CLOUDS.viscosity,
      diffusivity: DEFAULT_CLOUDS.diffusivity,
      nudgeTimeS: DEFAULT_CLOUDS.nudgeTimeS,
      jacobiIterations: DEFAULT_CLOUDS.jacobiIterations,
    };
  }
  function cloudGeometry(): { sizeM: number; baseM: number; topM: number } {
    return {
      sizeM: 2 * DOMAIN_RADIUS_IN_RMAX * state.rMaxM,
      baseM: DEFAULT_CLOUDS.baseM,
      topM: DEFAULT_CLOUDS.topM,
    };
  }
  fluid.init(cloudParams(TIERS[activeTier].fluidGridSize));

  // The startup benchmark and the calibration sweep never run together.
  let benchmarking = !BENCH_SWEEP;
  const sweep = BENCH_SWEEP ? new BenchSweep() : null;
  const benchRun = BENCH_RUN ? new BenchRun(BENCH_WARMUP_S, BENCH_MEASURE_S) : null;
  const benchPanel = sweep || benchRun ? createBenchPanel() : null;
  let benchRunStartMs = -1;
  let benchStatusAt = 0;
  let sweepReported = false;
  const targetMs = DEFAULT_GOVERNOR_OPTIONS.targetFrameMs;
  let benchStartMs = -1;
  const sampler = new FrameSampler(Math.ceil((BENCHMARK_DURATION_MS / 1000) * 240));
  let gpuLost: (reason: string) => void = () => undefined;

  async function buildGpu(tier: TierId): Promise<void> {
    const t = TIERS[tier];
    const r = await createRenderer(
      container,
      t.renderScale,
      (reason) => {
        gpuLost(reason);
      },
      FORCE_WEBGL,
    );
    const s = createScene(r.canvas, state.rMaxM);
    s.setAspect(r.resize());
    const tracers = GPU_PHYSICS
      ? createGpuTracers(r.renderer, wind, debrisTracers.options, MAX_DEBRIS_ANY_TIER)
      : null;
    // GPU mode: one mesh at full capacity for the life of the tracers. Disposing
    // a mesh that reads the storage buffers makes three.js destroy those buffers
    // (Geometries.js, node-attribute cleanup on geometry dispose), so tier
    // changes only change the draw count.
    const debris = tracers
      ? createDebris(MAX_DEBRIS_ANY_TIER, SEED, tracers)
      : createDebris(t.maxDebris, SEED);
    s.scene.add(debris.mesh);
    const vortex = createVortexDebug(wind, inset);
    vortex.rebuild(simTimeS);
    vortex.setVisible(state.debugOverlay);
    s.scene.add(vortex.group);
    const timer = createGpuTimer(r.renderer);
    const clouds = createClouds(
      TIERS[tier],
      fluid.texture,
      cloudGeometry(),
      state.verticalStretch,
      RENDER_UNITS_PER_M,
    );
    clouds.setVisible(state.clouds);
    s.scene.add(clouds.group);
    const cowLod = createCowLod(eParams, TIERS[tier].cowLod, cowKmPerM, makeCowTexture());
    cowLod.setVisible(false);
    s.scene.add(cowLod.root);
    gpu = { r, s, debris, vortex, tracers, timer, clouds, cowLod };
    // New scene, new cow mesh: place it without rolling from the origin.
    cowRespawned = true;
    hud.setStatic({
      backend: r.backend,
      physics: tracers ? 'GPU compute' : 'CPU',
      gpuTiming: timer.available,
    });
  }

  /** Read `gpu` through a call so TS does not narrow it from closure assignments. */
  function currentGpu(): typeof gpu {
    return gpu;
  }

  function renderStill(): void {
    if (gpu) gpu.r.renderer.render(gpu.s.scene, gpu.s.camera);
  }

  function disposeGpu(): void {
    if (!gpu) return;
    gpu.debris.dispose();
    gpu.vortex.dispose();
    gpu.clouds.dispose();
    gpu.cowLod.dispose();
    gpu.s.dispose();
    gpu.r.dispose();
    gpu = null;
  }

  /** Not hot path: runs on tier change only. Rebuilds the instanced mesh. */
  function applyTier(tier: TierId): void {
    activeTier = tier;
    const t = TIERS[tier];
    if (t.fluidGridSize !== fluid.n) fluid.init(cloudParams(t.fluidGridSize));
    gpu?.clouds.setDensity(fluid.texture);
    gpu?.clouds.setTier(t);
    gpu?.cowLod.setLod(t.cowLod);
    // The CPU tracers stay authoritative only on the CPU path; on the GPU
    // path new tracers are spawned by the GPU kernel.
    if (!gpu?.tracers) debrisTracers.setCount(t.maxDebris);
    if (gpu) {
      gpu.r.setRenderScale(t.renderScale);
      if (gpu.tracers) {
        gpu.tracers.setCount(t.maxDebris);
      } else {
        gpu.s.scene.remove(gpu.debris.mesh);
        gpu.debris.dispose();
        gpu.debris = createDebris(t.maxDebris, SEED);
        gpu.s.scene.add(gpu.debris.mesh);
      }
    }
    const live = gpu?.tracers ? gpu.tracers.count : debrisTracers.count;
    hud.setStatic({ tier, debrisLive: live, debrisCapacity: t.maxDebris });
  }

  const loop = createLoop({
    fixedDtS: DEFAULT_FIXED_DT_S,
    maxSubsteps: DEFAULT_MAX_SUBSTEPS,
    update(dtS: number): void {
      const simDt = dtS * state.timeScale;
      wind.setGust(gust.step(simDt, gustRng));
      if (gpu?.tracers) gpu.tracers.step(simDt, simTimeS);
      else debrisTracers.step(simDt, simTimeS, wind);
      if (cowParams.mode === 'realistic') {
        stepEllipsoidCow(eParams, eCow, wind, simTimeS, simDt);
        cow.posX = eCow.posX;
        cow.posY = eCow.posY;
        cow.posZ = eCow.posZ;
        cow.prevX = eCow.prevX;
        cow.prevY = eCow.prevY;
        cow.prevZ = eCow.prevZ;
        cow.onGround = eCow.onGround;
      } else {
        stepRigidBody(cowParams, cow, wind, simTimeS, simDt);
      }
      fluid.tick(simDt, performance.now());
      simTimeS += simDt;
      cowAgeS += simDt;

      wind.stormCenter(simTimeS, center);
      const domainM = DOMAIN_RADIUS_IN_RMAX * state.rMaxM;
      const ox = cow.posX - center.x;
      const oz = cow.posZ - center.z;
      if (ox * ox + oz * oz > domainM * domainM || cowAgeS > COW_MAX_AGE_S) respawnCow();
      if (center.x * center.x + center.z * center.z > REBASE_DISTANCE_M * REBASE_DISTANCE_M) {
        rebase();
      }
    },
    render(alpha: number): void {
      if (!gpu) return;
      const k = RENDER_UNITS_PER_M;
      wind.stormCenter(simTimeS, center);
      gpu.s.followStorm(center.x * k, center.z * k);
      gpu.vortex.setCenter(center.x * k, center.z * k);
      if (benchRun) {
        // Scripted orbit so every run sees the same views. Allocation-free.
        const domainKm = DOMAIN_RADIUS_IN_RMAX * state.rMaxM * k;
        const ang = (performance.now() / 1000) * ((2 * Math.PI) / 120);
        const cxk = center.x * k;
        const czk = center.z * k;
        gpu.s.camera.position.set(
          cxk + 1.6 * domainKm * Math.cos(ang),
          0.8 * domainKm,
          czk + 1.6 * domainKm * Math.sin(ang),
        );
        gpu.s.controls.target.set(cxk, 0, czk);
      }
      gpu.clouds.setCenter(center.x * k, center.z * k);
      gpu.clouds.updateCamera(gpu.s.camera);
      gpu.s.controls.update();
      if (gpu.tracers) gpu.debris.setGpuFrame(alpha, state.verticalStretch, gpu.tracers.count);
      else gpu.debris.write(debrisTracers, alpha, state.verticalStretch);
      const b = 1 - alpha;
      const cx = (cow.prevX * b + cow.posX * alpha) * k;
      const cy = (cow.prevY * b + cow.posY * alpha) * k * state.verticalStretch;
      const cz = (cow.prevZ * b + cow.posZ * alpha) * k;
      const realistic = cowParams.mode === 'realistic';
      gpu.s.cow.visible = !realistic;
      gpu.cowLod.setVisible(realistic);
      if (realistic) {
        const floor = currentSupportHeight(eParams, eCow) * cowKmPerM;
        gpu.cowLod.root.position.set(cx, cy > floor ? cy : floor, cz);
        gpu.cowLod.root.quaternion.set(eCow.qx, eCow.qy, eCow.qz, eCow.qw);
        cowRespawned = false;
      } else if (cowRespawned) {
        gpu.s.snapCow(cx, cy, cz);
        cowRespawned = false;
      } else {
        gpu.s.placeCow(cx, cy, cz, cow.onGround);
      }
      gpu.r.renderer.render(gpu.s.scene, gpu.s.camera);
      gpu.clouds.renderOverlay(gpu.r.renderer, gpu.s.camera);
    },
    onFrame(frameDeltaS: number, substeps: number, cpuMs: number): void {
      const now = performance.now();
      const frameMs = frameDeltaS * 1000;
      if (frameMs <= 0) return;
      gpu?.timer.frameEnd();
      const gpuMs = gpu ? gpu.timer.gpuMs : Number.NaN;
      const signal = governorSignalMs(frameMs, cpuMs, gpuMs, targetMs);
      hud.noteTiming(cpuMs, gpuMs, signal);

      if (sweep) {
        if (!sweep.done) {
          const next = sweep.frame(now, frameMs, cpuMs, gpuMs);
          if (next !== null) {
            applyTier(next);
            benchPanel?.setStatus(sweep.status);
          }
        } else if (!sweepReported) {
          sweepReported = true;
          reportSweep(sweep);
        }
      } else if (benchmarking) {
        if (benchStartMs < 0) benchStartMs = now;
        sampler.push(signal);
        if (now - benchStartMs >= BENCHMARK_DURATION_MS || sampler.full) finishBenchmark();
      } else if (state.tierOverride === 'auto') {
        const from = activeTier;
        const next = governor.observe(signal, frameMs > targetMs * 1.5);
        if (next !== null) {
          applyTier(next);
          hud.noteGovernorChange(from, next, governor.ema, now);
        }
        if (benchRun && !benchRun.done) {
          if (benchRunStartMs < 0) benchRunStartMs = now;
          const finished = benchRun.frame(
            now,
            now - benchRunStartMs,
            frameMs,
            activeTier,
            next !== null,
          );
          if (now - benchStatusAt > 1000) {
            benchStatusAt = now;
            benchPanel?.setStatus(benchRun.status(now - benchRunStartMs));
          }
          if (finished) reportBenchRun(benchRun);
        }
      }
      hud.noteBlocked(governor.blockedTier, governor.blockedForFrames);
      hud.noteDropRate(governor.droppedFrameRate);
      hud.noteFluid(fluid.lastStepMs, fluid.n);
      hud.noteGust(state.turbulenceMps > 0 ? gust.value : Number.NaN);
      hud.recordFrame(frameMs, substeps, governor.ema, now);
    },
  });

  function reportSweep(s: BenchSweep): void {
    const g = currentGpu();
    const nav = navigator as Navigator & { deviceMemory?: number };
    const result = s.result({
      userAgent: navigator.userAgent,
      backend: g ? g.r.backend : 'none',
      physics: g?.tracers ? 'gpu' : 'cpu',
      gpuTiming: g ? g.timer.available : false,
      hardwareConcurrency: nav.hardwareConcurrency > 0 ? nav.hardwareConcurrency : null,
      deviceMemoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
      devicePixelRatio: window.devicePixelRatio,
      canvasCssPx: [container.clientWidth, container.clientHeight],
    });
    (window as unknown as { sweepResult: unknown }).sweepResult = result;
    console.info('[sweep]', JSON.stringify(result));
    benchPanel?.showResult(result);
  }

  function reportBenchRun(b: BenchRun): void {
    const g = currentGpu();
    const result = b.result({
      backend: g ? g.r.backend : 'none',
      physics: g?.tracers ? 'gpu' : 'cpu',
      finalTier: activeTier,
      debris: TIERS[activeTier].maxDebris,
    });
    (window as unknown as { benchResult: unknown }).benchResult = result;
    console.info('[bench]', JSON.stringify(result));
    benchPanel?.showResult(
      result,
      'Benchmark finished. Copy this and paste it back (add device, OS, and browser).',
    );
  }

  function finishBenchmark(): void {
    benchmarking = false;
    profile.benchmarkMsPerFrame = sampler.median();
    hud.setStatic({ benchmarkMs: profile.benchmarkMsPerFrame });
    const chosen = chooseInitialTier(profile);
    console.info(
      `[tiering] benchmark median ${String(profile.benchmarkMsPerFrame)} ms at medium, starting at ${chosen}`,
      profile,
    );
    governor.reset(chosen);
    if (state.tierOverride === 'auto' && chosen !== activeTier) applyTier(chosen);
  }

  const lifecycle = installLifecycle({
    loop,
    overlay,
    isUserPaused: () => state.paused,
    async rebuildGpu(): Promise<boolean> {
      disposeGpu();
      await buildGpu(activeTier);
      applyTier(activeTier);
      return true;
    },
  });
  gpuLost = (reason) => {
    lifecycle.notifyGpuLost(reason);
  };

  try {
    await buildGpu(activeTier);
  } catch (err) {
    console.error('[main] renderer init failed', err);
    showPoster('The GPU renderer failed to start on this device. Details are in the console.');
    return;
  }
  applyTier(activeTier);

  window.addEventListener('resize', () => {
    if (gpu) gpu.s.setAspect(gpu.r.resize());
  });

  function setPaused(p: boolean): void {
    state.paused = p;
    playBtn.hidden = !p;
    if (p) loop.stop();
    else if (!document.hidden) loop.start();
  }

  const controls = createControls(byId('controls'), state, (key: ControlKey) => {
    switch (key) {
      case 'deltaPPa':
      case 'rMaxM':
      case 'B':
      case 'latitudeDeg': {
        holland.pCentralPa = holland.pAmbientPa - state.deltaPPa;
        holland.rMaxM = state.rMaxM;
        holland.B = state.B;
        holland.latitudeDeg = state.latitudeDeg;
        wind.refreshParams();
        if (key === 'rMaxM') {
          Object.assign(debrisTracers.options, tracerGeometry(state.rMaxM));
          debrisTracers.respawnAll();
          gpu?.tracers?.respawnAll();
          respawnCow();
        }
        gpu?.vortex.rebuild(simTimeS);
        if (key === 'rMaxM') {
          fluid.init(cloudParams(TIERS[activeTier].fluidGridSize));
          gpu?.clouds.setDensity(fluid.texture);
          gpu?.clouds.setGeometry(cloudGeometry(), state.verticalStretch);
        } else {
          fluid.updateParams(cloudParams(fluid.n));
        }
        break;
      }
      case 'steerMps':
      case 'steerHeadingDeg': {
        // Re-anchor so the storm continues from where it is, on the new heading.
        wind.stormCenter(simTimeS, center);
        const tr = setSteering(wind.translation, state.steerMps, state.steerHeadingDeg);
        tr.originXM = center.x - tr.steerXMps * simTimeS;
        tr.originZM = center.z - tr.steerZMps * simTimeS;
        gpu?.vortex.rebuild(simTimeS);
        break;
      }
      case 'inflowPeakMps':
        if (wind.inflow) {
          wind.inflow.params.inflowPeakMps = state.inflowPeakMps;
          wind.inflow.refresh();
        }
        gpu?.vortex.rebuild(simTimeS);
        fluid.updateParams(cloudParams(fluid.n));
        break;
      case 'turbulenceMps':
        if (wind.noise) {
          wind.noise.params.amplitudeMps = state.turbulenceMps;
          wind.noise.refresh();
        }
        break;
      case 'debugOverlay':
        gpu?.vortex.setVisible(state.debugOverlay);
        break;
      case 'clouds':
        gpu?.clouds.setVisible(state.clouds);
        break;
      case 'hudVisible':
        hud.setVisible(state.hudVisible);
        break;
      case 'paused':
        setPaused(state.paused);
        break;
      case 'tierOverride':
        hud.setStatic({ governorEnabled: state.tierOverride === 'auto' });
        if (state.tierOverride === 'auto') governor.reset(activeTier);
        else applyTier(state.tierOverride);
        break;
      case 'cowMode':
        cowParams.mode = state.cowMode;
        respawnCow();
        break;
      case 'verticalStretch':
        gpu?.clouds.setGeometry(cloudGeometry(), state.verticalStretch);
        break;
      case 'timeScale':
        break;
    }
  });

  createFinancePanel(byId('finance'), () => {
    const p = gust.params;
    return `mean ${p.mu}, reversion time ${(1 / p.theta).toFixed(0)} s, current value ${gust.value.toFixed(2)}`;
  });

  playBtn.addEventListener('click', () => {
    setPaused(false);
    controls.sync();
  });

  if (benchRun) {
    const gb = currentGpu();
    if (gb) gb.s.controls.enabled = false;
    benchPanel?.setStatus(benchRun.status(0));
  }

  if (sweep) {
    state.tierOverride = 'low';
    hud.setStatic({ governorEnabled: false });
    applyTier('low');
    const gs = currentGpu();
    if (gs) gs.s.controls.enabled = false;
    benchPanel?.setStatus(sweep.status);
  }

  const g0 = currentGpu();
  if (VALIDATE_GPU && g0) {
    overlay.textContent = 'Validating GPU tracers against the CPU reference…';
    overlay.hidden = false;
    try {
      const res = await runGpuValidation(g0.r.renderer, wind, g0.r.backend);
      (window as unknown as { gpuValidation: unknown }).gpuValidation = res;
      console.info('[validate] GPU vs CPU', JSON.stringify(res));
      overlay.textContent =
        `GPU vs CPU (${res.backend}): ${res.pass ? 'PASS' : 'FAIL'}. ` +
        `max ${res.maxErrM.toFixed(3)} m, p99 ${res.p99ErrM.toFixed(3)} m, ` +
        `median ${res.medianErrM.toFixed(4)} m over ${res.compared} tracers ` +
        `(${res.excludedRespawned} respawned, excluded). Tolerance ${res.toleranceM} m. ` +
        'Click to continue.';
    } catch (err) {
      console.error('[validate] failed', err);
      overlay.textContent = `Validation error: ${String(err)}. Click to continue.`;
    }
    overlay.addEventListener('click', () => (overlay.hidden = true), { once: true });
  }

  // Reduced motion: render one still frame, wait for the user to press play.
  if (state.paused) {
    playBtn.hidden = false;
    renderStill();
  } else {
    loop.start();
  }
}

void main();
